import * as React from 'react';
import { ArticleSubscriber } from '../../../../../amorg-common';
import { Button } from '../../../../../designSystem/button/Button';
import { AnimatedCollapsibleContent } from '../../../../../designSystem/collapsible/AnimatedCollapsibleContent';
import { Flex } from '../../../../../designSystem/compositeComponents/FlexHelpers';
import { H3 } from '../../../../../designSystem/typography/Heading';
import { InfoChip } from '../../../../../designSystem/Chip';
import { CountAvatar } from '../../../../../designSystem/avatar/CountAvatar';
import { Table } from '../../../../../designSystem/Table';
import { Link } from '../../../../../designSystem/Link';
import { Modal } from '../../../../../designSystem/Modal';
import { SelectField } from '../../../../../designSystem/formField/SelectField';
import { MessageCard } from '../../../../../designSystem/MessageCard';
import { articlesClientApi } from '../../../../../api/articlesClientApi';
import { ButtonRow } from '../../../../../designSystem/button/ButtonRow';


type ArticleViewSubscriberPanelProps = {
    subscribers: ArticleSubscriber[];
};

type ArticleViewSubscriberTableProps = ArticleViewSubscriberPanelProps & {
    articleId: string,
    isDeleted: boolean,
    onSubscribersChanged: () => Promise<void>;
};
export function ArticleViewSubscriberPanel (props: ArticleViewSubscriberPanelProps): JSX.Element
{
    const [show, setShow] = React.useState<boolean>(false);

    React.useEffect(() =>
    {
        setShow(props.subscribers.length < 8);
    }, [props.subscribers]);

    return (
        <Flex flexDirection="column">
            <Flex>
                <Flex gap={8} style={{ flexGrow: 1 }}>
                    <H3>Abonnenter</H3>
                    <CountAvatar count={props.subscribers.length} size='24px' />
                </Flex>
                <Button variant='text' icon={show ? 'caret_caretUp' : 'caret_caretDown'} onClick={() => setShow(!show)} />
            </Flex>
            <AnimatedCollapsibleContent open={show}>
                <Flex flexDirection='row' gap={8} style={{ flexGrow: 1, flexWrap: 'wrap' }}>
                    {props.subscribers.map((s) =>
                    {
                        return (
                            <InfoChip key={s.subscriberId}>
                                {s.companyName || s.siteId}
                            </InfoChip>
                        );
                    })}
                </Flex>

            </AnimatedCollapsibleContent>
        </Flex>
    );
}

type AvailableSubscriber = {
    subscriberId: string,
    siteId: string,
    companyName: string | null;
};

export function ArticleViewSubscriberTable(props: ArticleViewSubscriberTableProps): JSX.Element
{
    const [showAddModal, setShowAddModal] = React.useState(false);
    const [availableSubscribers, setAvailableSubscribers] = React.useState<AvailableSubscriber[]>([]);
    const [selectedSubscriberId, setSelectedSubscriberId] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [message, setMessage] = React.useState('');

    async function handleShowAddModal ()
    {
        setShowAddModal(true);
        setSelectedSubscriberId('');
        setAvailableSubscribers([]);
        setMessage('');
        setLoading(true);
        try
        {
            setAvailableSubscribers(await articlesClientApi.getAvailableSubscribersForArticle(props.articleId));
        }
        catch
        {
            setMessage('Kunne ikke hente abonnenter');
        }
        finally
        {
            setLoading(false);
        }
    }

    async function handleAddSubscriber ()
    {
        setMessage('');
        setLoading(true);
        try
        {
            const result = await articlesClientApi.toggleArticleSubscription(selectedSubscriberId, props.articleId, true);
            if (result.status !== 'success')
            {
                setMessage(result.status === 'failure' ? result.clientMessage : 'Kunne ikke tilføje abonnenten');
                return;
            }

            setShowAddModal(false);
            await props.onSubscribersChanged();
        }
        catch
        {
            setMessage('Kunne ikke tilføje abonnenten');
        }
        finally
        {
            setLoading(false);
        }
    }

    return (
        <>
            <Table>
                <thead>
                    <tr>
                        <th>Site</th>
                        <th>Organisation</th>
                        <th style={{width:'1%', whiteSpace: 'nowrap'}}></th>
                    </tr>
                </thead>
                <tbody>
                    {props.subscribers.map((s) => (
                        <tr key={s.subscriberId}>
                            <td>{s.siteId}</td>
                            <td>{s.companyName}</td>
                            <td><Link href={`/amorgy/articles/subscribers/${s.subscriberId}`}>Rediger abonnement</Link></td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            {!props.isDeleted &&
                <ButtonRow marginTop={16}>
                    <Button variant='filled' text='Tilføj abonnent' onClick={handleShowAddModal} />
                </ButtonRow>
            }
            <Modal
                title='Tilføj abonnent'
                showModal={showAddModal}
                onCancel={() => setShowAddModal(false)}
                content={
                    <Flex flexDirection='column' gap={16}>
                        {message &&
                            <MessageCard variant='warning'>{message}</MessageCard>
                        }
                        {availableSubscribers.length > 0 &&
                            <SelectField
                                label='Abonnent'
                                selectedValue={selectedSubscriberId}
                                options={availableSubscribers.map(subscriber => ({
                                    value: subscriber.subscriberId,
                                    text: `${subscriber.siteId}`
                                }))}
                                emptyText='Vælg abonnent'
                                onChange={setSelectedSubscriberId}
                            />
                        }
                        {!loading && availableSubscribers.length === 0 && !message &&
                            <div>Der er ingen aktive abonnenter, som ikke allerede abonnerer på lovkravet.</div>
                        }
                        <Flex justifyContent='space-between' gap={16}>
                            <Button variant='text' text='Annullér' onClick={() => setShowAddModal(false)} />
                            <Button
                                variant='filled'
                                text='Tilføj abonnent'
                                disabled={loading || !selectedSubscriberId}
                                onClick={handleAddSubscriber}
                            />
                        </Flex>
                    </Flex>
                }
            />
        </>
    );
}